import { useState } from 'react';
import type { AdminStore, StorePlanOption } from '@/client/types/stores';
import { useStorePlanQuery } from '@/client/api/stores/stores.query';
import {
  useSetPlanExempt,
  useUpdateStoreCommission,
  useUpdateStorePlan,
} from '@/client/api/stores/stores.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { Input } from '@/components/ui/input';
import { InfoRow, SectionCard, CardSkeleton, InfoRowsSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';

/** `YYYY-MM-DD` for a date input from an ISO timestamp. */
const toDateInput = (iso?: string | null): string => (iso ? iso.slice(0, 10) : '');

const PlanOverrideForm: React.FC<{ store: AdminStore; plid?: number | null; tier?: string | null; expires?: string | null; options?: StorePlanOption[] }> = ({
  store,
  options,
  plid,
  tier,
  expires,
}) => {
  const update = useUpdateStorePlan();
  const [form, setForm] = useState({
    plid: plid != null ? String(plid) : '',
    plan_tier: tier ?? '',
    date_plan_expires: toDateInput(expires),
  });
  const [confirm, setConfirm] = useState(false);

  const initial = { plid: plid != null ? String(plid) : '', plan_tier: tier ?? '', date_plan_expires: toDateInput(expires) };
  const changes: { plid?: number | null; plan_tier?: string | null; date_plan_expires?: string | null } = {};
  if (form.plid !== initial.plid) changes.plid = form.plid ? Number(form.plid) : null;
  if (form.plan_tier !== initial.plan_tier) changes.plan_tier = form.plan_tier.trim() || null;
  if (form.date_plan_expires !== initial.date_plan_expires)
    changes.date_plan_expires = form.date_plan_expires ? `${form.date_plan_expires}T23:59:59` : null;
  const dirty = Object.keys(changes).length > 0;

  return (
    <SectionCard title="Plan override" description="Comp a plan, change the tier, or extend the expiry. The seller's billing is not charged.">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium text-gray-500">Plan</span>
          {options?.length ? (
            <select
              value={form.plid}
              onChange={(e) => setForm((f) => ({ ...f, plid: e.target.value }))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">None</option>
              {options.map((o) => (
                <option key={o.plid} value={String(o.plid)}>
                  {o.name ?? 'Plan'} · #{o.plid}{o.expired ? ' · expired' : o.canceled ? ' · canceled' : o.active ? '' : ' · inactive'}
                </option>
              ))}
            </select>
          ) : (
            // The owner has no store-plan subscription to pick from.
            <Input value={form.plid} onChange={(e) => setForm((f) => ({ ...f, plid: e.target.value.replace(/\D/g, '') }))} placeholder="Plan id (plid)" />
          )}
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium text-gray-500">Tier</span>
          <Input value={form.plan_tier} onChange={(e) => setForm((f) => ({ ...f, plan_tier: e.target.value }))} placeholder="e.g. growth" />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium text-gray-500">Expires</span>
          <Input type="date" value={form.date_plan_expires} onChange={(e) => setForm((f) => ({ ...f, date_plan_expires: e.target.value }))} />
        </label>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" disabled={!dirty} onClick={() => setForm(initial)} className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40">
          Reset
        </button>
        <PermissionButton code={PERMISSIONS.store.plan} disabled={!dirty} onClick={() => setConfirm(true)}>
          Apply override
        </PermissionButton>
      </div>
      {confirm && (
        <ReasonDialog
          open
          title="Override store plan"
          description={`Changes: ${Object.keys(changes).join(', ')}.`}
          confirmLabel="Apply"
          reasonRequired
          loading={update.isPending}
          onConfirm={(reason) => update.mutate({ storeid: store.storeid, ...changes, reason }, { onSuccess: () => setConfirm(false) })}
          onClose={() => setConfirm(false)}
        />
      )}
    </SectionCard>
  );
};

const CommissionForm: React.FC<{ store: AdminStore; planRate?: number }> = ({ store, planRate }) => {
  const update = useUpdateStoreCommission();
  const current = store.commission_rate_override;
  const [rate, setRate] = useState(current != null ? String(current) : '');
  const [confirm, setConfirm] = useState<null | 'set' | 'clear'>(null);
  const parsed = Number(rate);
  const validRate = rate.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;

  return (
    <SectionCard
      title="Commission override"
      description={`Plan rate: ${planRate != null ? `${planRate}%` : '—'}. An override replaces it for this store only.`}
      actions={<span className="text-xs text-gray-400">{current != null ? `Override ${current}%` : 'No override'}</span>}
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium text-gray-500">Rate (%)</span>
          <Input type="number" min={0} max={100} step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="w-32" />
        </label>
        <PermissionButton code={PERMISSIONS.store.commission} disabled={!validRate} onClick={() => setConfirm('set')}>
          Set override
        </PermissionButton>
        {current != null && (
          <PermissionButton code={PERMISSIONS.store.commission} variant="outline" onClick={() => setConfirm('clear')}>
            Clear override
          </PermissionButton>
        )}
      </div>
      {confirm && (
        <ReasonDialog
          open
          title={confirm === 'set' ? `Set commission to ${parsed}%` : 'Clear commission override'}
          description={confirm === 'set' ? 'Applies to new orders from this store.' : 'The store goes back to its plan rate.'}
          confirmLabel={confirm === 'set' ? 'Set' : 'Clear'}
          destructive={confirm === 'clear'}
          reasonRequired={confirm === 'set'}
          loading={update.isPending}
          onConfirm={(reason) =>
            update.mutate(
              { storeid: store.storeid, commission_rate: confirm === 'set' ? parsed : null, reason },
              { onSuccess: () => setConfirm(null) },
            )
          }
          onClose={() => setConfirm(null)}
        />
      )}
    </SectionCard>
  );
};

const limit = (n?: number): string => (n === -1 ? 'Unlimited' : n == null ? '—' : String(n));

export const PlanTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const { data, isLoading } = useStorePlanQuery(store.storeid);
  const setExempt = useSetPlanExempt();
  const [dialog, setDialog] = useState<null | 'grant' | 'revoke'>(null);

  if (isLoading || !data)
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[5, 5, 3, 3].map((rows, i) => <CardSkeleton key={i}><InfoRowsSkeleton rows={rows} /></CardSkeleton>)}
      </div>
    );

  const plan = data.plan ?? {};
  const ent = data.entitlements ?? {};
  const exempt = Boolean(plan.plan_exempt);

  const run = (reason: string) => {
    setExempt.mutate(
      { storeid: store.storeid, plan_exempt: dialog === 'grant', reason },
      { onSuccess: () => setDialog(null) },
    );
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <SectionCard title="Plan">
        <div className="divide-y divide-gray-100">
          <InfoRow label="Tier">{exempt ? 'Exempt' : plan.plan_tier ?? (plan.is_free ? 'Free' : '—')}</InfoRow>
          <InfoRow label="Plan name">{plan.plan_name ?? '—'}</InfoRow>
          <InfoRow label="Commission rate">
            {store.commission_rate_override != null
              ? `${store.commission_rate_override}% (override)`
              : ent.commission_rate != null ? `${ent.commission_rate}%` : '—'}
          </InfoRow>
          <InfoRow label="Expires">
            {plan.date_plan_expires ? new Date(plan.date_plan_expires).toLocaleDateString() : '—'}
          </InfoRow>
          <InfoRow label="Status">{plan.is_expired ? 'Expired' : plan.active ? 'Active' : '—'}</InfoRow>
        </div>
      </SectionCard>

      <SectionCard title="Entitlements & limits">
        <div className="divide-y divide-gray-100">
          <InfoRow label="Max listings">{limit(ent.max_listings)}</InfoRow>
          <InfoRow label="Max orders / month">{limit(ent.max_orders_per_month)}</InfoRow>
          <InfoRow label="Staff accounts">{limit(ent.staff_accounts)}</InfoRow>
          <InfoRow label="Analytics">{ent.analytics ?? '—'}</InfoRow>
        </div>
      </SectionCard>

      <SectionCard
        title="Plan exemption"
        description="Exempt stores bypass all plan limits with zero commission."
        actions={<span className="text-xs text-gray-400">{exempt ? 'Exempt' : 'Not exempt'}</span>}
      >
        {exempt ? (
          <PermissionButton code={PERMISSIONS.store.plan} variant="outline" onClick={() => setDialog('revoke')}>Revoke exemption</PermissionButton>
        ) : (
          <PermissionButton code={PERMISSIONS.store.plan} onClick={() => setDialog('grant')}>Grant exemption</PermissionButton>
        )}
      </SectionCard>

      <CommissionForm store={store} planRate={ent.commission_rate} />

      <div className="md:col-span-2">
        <PlanOverrideForm
          key={`${plan.plid ?? ''}-${plan.plan_tier ?? ''}-${plan.date_plan_expires ?? ''}`}
          store={store}
          plid={plan.plid}
          options={data.plan_options}
          tier={plan.plan_tier}
          expires={plan.date_plan_expires}
        />
      </div>

      {dialog && (
        <ReasonDialog
          open
          title={dialog === 'grant' ? 'Grant plan exemption' : 'Revoke plan exemption'}
          description={dialog === 'grant' ? 'Store gets unlimited limits and 0% commission.' : 'Store returns to its plan limits.'}
          confirmLabel={dialog === 'grant' ? 'Grant' : 'Revoke'}
          destructive={dialog === 'revoke'}
          reasonRequired={dialog === 'grant'}
          loading={setExempt.isPending}
          onConfirm={run}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
};
